import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { sendTrackingInfo } from '@panel/commons';
import { isSiteWithTelBanner, getDevice } from '@panel/commons/helpers';
import OptionsSelector from '@panel/commons/lib/DropDown/OptionSelector';
import { select } from '@panel/commons/core/themev2';
import CopyShareIcon from '@panel/commons/assets/icons/share.svg';
import CopyShareMobileIcon from '@panel/commons/assets/icons/share-mobile.svg';
import EmailIcon from '@panel/commons/assets/icons/email.svg';
import WhatsAppIcon from '@panel/commons/assets/icons/whatsapp.svg';
import uuid from 'uuid-random';
import { DesempenoSelectors, DesempenoOperations } from '../../ducks/desempeno';

const ButtonShareView = ({
  postingId,
  publisherId,
  dataToShare,
  setFinishedAction,
  setShowShareOptions = () => {},
  showShareOptions = false,
  handleShareButtonOutsideClick = () => {}
}) => {
  const device = getDevice();
  const dispatch = useDispatch();
  const isDesktop = device === 'desktop';
  const isTablet = device === 'tablet';
  const isMobile = device === 'mobile';
  const { t } = useTranslation();
  const positionSelectRef = useRef(null);
  const { saveSharedPerformance } = DesempenoOperations;
  const { hashToShareInfoPerformance, hasInfoShared, loadingInfoShared } = useSelector(
    DesempenoSelectors.getDesempenoState
  );
  const [urlToShareInfo, setUrlToShareInfo] = useState(false);

  /**
   * La función redirecciona a WhatsApp (Web y Mobile) para compartir link con el desempeño del aviso.
   */
  const shareWhatsAppLink = e => {
    e.stopPropagation();
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Compartir-whatsapp');
    if (hasInfoShared) {
      if (isDesktop || isTablet) {
        window.open(
          t('panel.desempeno.shareLink.whatsApp.urlBase.desktop') +
            t('panel.desempeno.shareLink.whatsApp.text', { url: urlToShareInfo }),
          '_blank'
        );
      }
      if (isMobile) {
        window.open(
          t('panel.desempeno.shareLink.whatsApp.urlBase.mobile') +
            t('panel.desempeno.shareLink.whatsApp.text', { url: urlToShareInfo }),
          '_blank'
        );
      }
    }
    setShowShareOptions(false);
  };

  const copyLink = e => {
    e.stopPropagation();
    navigator.clipboard.writeText(urlToShareInfo);
    setShowShareOptions(false);
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Compartir-link');
    setFinishedAction({
      postingId: '',
      action: 'link_copied',
      result: 'SUCCESS',
      component: t('panel.desempeno.shareLink.copylink.copied'),
      keepOpen: false
    });
  };

  const optionsToShare = [
    {
      text: t('panel.desempeno.shareLink.copylink'),
      icon: CopyShareIcon,
      handleOnClick: e => copyLink(e),
      id: uuid()
    },
    {
      text: t('panel.desempeno.shareLink.email'),
      icon: EmailIcon,
      handleOnClick: e => {
        e.stopPropagation();
        window.location.href = `mailto:?subject=${t('panel.desempeno.shareLink.email.subjet', {
          url: urlToShareInfo
        })}}`;
        sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Compartir-mail');
        setShowShareOptions(false);
      },
      id: uuid()
    },
    {
      text: t('panel.desempeno.shareLink.whatsApp'),
      icon: WhatsAppIcon,
      handleOnClick: e => shareWhatsAppLink(e),
      id: uuid()
    }
  ];

  const handleOnClickShare = e => {
    e.stopPropagation();
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Compartir');
    if (!hasInfoShared) dispatch(saveSharedPerformance(postingId, publisherId, dataToShare));
    setShowShareOptions(!showShareOptions);
  };

  useEffect(() => {
    if (hasInfoShared) {
      setUrlToShareInfo(
        `${window.location.origin}/panel/compartir-desempeno?shared_token=${hashToShareInfoPerformance}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashToShareInfoPerformance, hasInfoShared]);

  return (
    <ContentLayout
      isDesktop={isDesktop}
      portalWithBanner={isSiteWithTelBanner}
      onClick={handleOnClickShare}
      data-panel='panel-shareButtonPanel'
      hoverActive={showShareOptions}
    >
      {isDesktop && t('panel.desempeno.shareLink')}
      <StyledLink
        icon={isDesktop ? 'shareIcon' : 'shareMobileIcon'}
        backSize={`${isDesktop ? 20 : 23}px`}
        data-panel='panel-shareButtonPanel-sharePerformance'
        isDesktop={isDesktop}
      />
      {showShareOptions && (
        <OptionsSelector
          isOpen={showShareOptions}
          optionsArray={optionsToShare}
          elementRef={positionSelectRef}
          handleOutsideClick={handleShareButtonOutsideClick}
          height='213px'
          width='229px'
          title={t('panel.desempeno.shareLink.title')}
          textInfo={t('panel.desempeno.shareLink.bajada')}
          justifyContent='left'
          topUp='35px'
          topDown={isDesktop ? '48px' : '53px'}
          right={isDesktop ? 'unset' : '0'}
          left={!isDesktop ? 'unset' : '0'}
          padding='0'
          hasSetUnmountComponent
          isLoading={loadingInfoShared}
        />
      )}
    </ContentLayout>
  );
};

const ContentLayout = styled.div`
  width: ${({ isDesktop }) => (isDesktop ? '119px' : '48px')};
  height: ${({ isDesktop }) => (isDesktop ? '40px' : '48px')};
  border-radius: 8px;
  padding: 0 12px;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
  color: ${select('black')};
  text-align: center;
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
  margin-right: ${({ isDesktop }) => (isDesktop ? '8px' : '4px')};
  cursor: pointer;
  ${({ hoverActive }) =>
    hoverActive &&
    css`
      background-color: ${select('neutralGray')};
    `}
  &:hover {
    background-color: ${({ isDesktop }) => (isDesktop ? select('neutralGray') : 'none')};
    ${({ isMobile }) =>
      isMobile &&
      css`
        position: fixed;
        top: 70px;
        border-radius: 0;
        left: 187px;
        z-index: 4;
      `}
    ${({ isTablet }) =>
      isTablet &&
      css`
        position: fixed;
        top: 70px;
        border-radius: 0;
        left: 577px;
        z-index: 4;
      `}
  }
`;

const Icons = {
  shareIcon: { iconId: CopyShareIcon },
  shareMobileIcon: { iconId: CopyShareMobileIcon },
  emailIcon: { iconId: EmailIcon },
  whatsAppIcon: { iconId: WhatsAppIcon }
};

const StyledLink = styled.div`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background-image: ${({ icon, disabled }) => `url(${!disabled ? Icons[icon].iconId : Icons[icon].disabled})`};
  width: 40px;
  height: 40px;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 50%;
  background-size: ${({ backSize }) => backSize};
  position: relative;
  margin-left: ${({ isDesktop }) => isDesktop && '4px'};
`;

ButtonShareView.propTypes = {
  postingId: PropTypes.string.isRequired,
  publisherId: PropTypes.string.isRequired
};

export default ButtonShareView;
